/**
 * The only place that formats dates, times, numbers and money (REQ-05-019).
 * Direct `Intl.*` or `toLocale*` use anywhere else fails lint. Timestamps from
 * the API are UTC (RFC 3339); they are shown in the tenant time zone.
 */
export interface FormatContext {
  readonly locale: string;
  readonly timeZone: string;
}

/** Until module 10 provides the tenant settings: German, Vienna. */
const DEFAULT_CONTEXT: FormatContext = { locale: 'de-AT', timeZone: 'Europe/Vienna' };

let current: FormatContext = DEFAULT_CONTEXT;

/** Called once the active tenant is known (module 10: `GET /v1/me`). */
export function setFormatContext(context: Partial<FormatContext>): void {
  current = { ...current, ...context };
}

export function formatContext(): FormatContext {
  return current;
}

/** The browser's own time zone, to warn when it differs from the tenant's (calendar header, spec 60 §6.2). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export interface Money {
  /** Decimal string with fixed decimals, never a float (`docs/api-guidelines.md` §3). */
  readonly amount: string;
  readonly currency: string;
}

function dateTimeFormat(options: Intl.DateTimeFormatOptions, context: FormatContext): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(context.locale, { ...options, timeZone: context.timeZone });
}

/** `26.09.2026, 16:30` for `2026-09-26T14:30:00Z` in Europe/Vienna. */
export function formatDateTime(isoUtc: string, context: FormatContext = current): string {
  return dateTimeFormat(
    { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    context,
  ).format(new Date(isoUtc));
}

export function formatDate(isoUtc: string, context: FormatContext = current): string {
  return dateTimeFormat({ day: '2-digit', month: '2-digit', year: 'numeric' }, context).format(new Date(isoUtc));
}

/** Whole calendar days from today to the day of `isoUtc`, both in the tenant time zone (0 = today, negative = past). */
export function calendarDaysUntil(isoUtc: string, context: FormatContext = current, now: Date = new Date()): number {
  const dayOf = (date: Date): number => {
    const parts = dateTimeFormat({ year: 'numeric', month: 'numeric', day: 'numeric' }, context).formatToParts(date);
    const part = (type: string) => Number(parts.find((entry) => entry.type === type)?.value);
    return Date.UTC(part('year'), part('month') - 1, part('day'));
  };
  return Math.round((dayOf(new Date(isoUtc)) - dayOf(now)) / 86_400_000);
}

export function formatTime(isoUtc: string, context: FormatContext = current): string {
  return dateTimeFormat({ hour: '2-digit', minute: '2-digit' }, context).format(new Date(isoUtc));
}

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
];

/** "vor 2 Stunden" for a past `isoUtc` timestamp (notifications list, spec 50 §6.5); "gerade eben" under a minute. */
export function formatRelativeTime(isoUtc: string, context: FormatContext = current, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(isoUtc).getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(context.locale, { numeric: 'auto' });
  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (seconds >= unitSeconds) return formatter.format(-Math.floor(seconds / unitSeconds), unit);
  }
  return formatter.format(0, 'minute');
}

export function formatNumber(value: number, context: FormatContext = current): string {
  return new Intl.NumberFormat(context.locale).format(value);
}

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

/**
 * Formats a decimal string (e.g. `size_sqm` `"54.5"` → `54,5` in `de-AT`)
 * without passing it through a float, like `formatMoney`. Trailing zeros are
 * dropped (`"40.0"` → `40`) unless `minimumFractionDigits` keeps them (`"90.00"` →
 * `90,00`); `grouping: false` suits values put back into an input.
 */
export function formatDecimal(
  value: string,
  options: { maximumFractionDigits?: number; minimumFractionDigits?: number; grouping?: boolean } = {},
  context: FormatContext = current,
): string {
  if (!DECIMAL_STRING.test(value)) {
    throw new Error(`formatDecimal: not a decimal string: ${value}`);
  }
  return new Intl.NumberFormat(context.locale, {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
    useGrouping: options.grouping ?? true,
  }).format(value as Intl.StringNumericLiteral);
}

/** The locale's decimal and grouping separators, from `Intl` itself. */
function separators(context: FormatContext): { decimal: string; group: string } {
  const parts = new Intl.NumberFormat(context.locale).formatToParts(12345.6);
  return {
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
  };
}

/**
 * Reads a decimal typed in the tenant locale (`54,5`, `1.234,5` in `de-AT`)
 * back into an API decimal string (`"54.5"`). A plain `.` decimal point is
 * accepted too when the locale uses a comma and the value has no grouping.
 * Returns `null` for anything that is not a non-negative number with at most
 * `maximumFractionDigits` decimals.
 */
export function parseDecimal(input: string, maximumFractionDigits = 1, context: FormatContext = current): string | null {
  const { decimal, group } = separators(context);
  let text = input.trim().replace(/\s/g, '');
  if (text.length === 0) return null;
  if (decimal !== '.' && !text.includes(decimal) && /^\d+\.\d+$/.test(text) && text.split('.')[1]!.length !== 3) {
    text = text.replace('.', decimal);
  }
  text = text.split(group).join('').replace(decimal, '.');
  const pattern = new RegExp(`^\\d+(\\.\\d{1,${maximumFractionDigits}})?$`);
  if (!pattern.test(text)) return null;
  const [whole, fraction] = text.split('.');
  const normalizedWhole = whole!.replace(/^0+(?=\d)/, '');
  return fraction ? `${normalizedWhole}.${fraction}` : normalizedWhole;
}

/**
 * A calendar day without time zone (`YYYY-MM-DD`, e.g. a campaign's
 * `starts_on`) as `01.12.2026`. It is the same day everywhere, so it is
 * formatted in UTC, never shifted into the tenant zone.
 */
export function formatCalendarDate(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${localDate}T00:00:00Z`),
  );
}

/** A calendar day's month and year, e.g. `01.12.2026` -> `Dezember 2026` (calendar header, no time zone shift). */
export function formatMonthYear(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/** A calendar day's short month name, e.g. `2026-03-01` -> `Mar` (month headers of the yearly grid, no time zone shift). */
export function formatMonthShort(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { month: 'short', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/** A calendar day's day and short month, e.g. `05.10.2026` -> `5. Okt.` (week range label, no time zone shift). */
export function formatDayMonth(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/**
 * A calendar day's full weekday and date, e.g. `2026-10-05` -> `Montag, 5. Oktober
 * 2026` (calendar grid day names, `Calendar` component labels, no time zone
 * shift — the value is already the tenant's wall date).
 */
export function formatWeekdayDate(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${localDate}T00:00:00Z`),
  );
}

/** A calendar day's short weekday, day and month, e.g. `2026-10-07` -> `Mi., 7. Okt.` (start page lists). */
export function formatWeekdayDayMonth(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/** A calendar day's short weekday, e.g. `2026-10-05` -> `Mo` (calendar column headers). */
export function formatWeekdayShort(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/** A calendar day's full weekday, e.g. `2026-10-05` -> `Montag` (posting time proposals, E510). */
export function formatWeekdayLong(localDate: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${localDate}T00:00:00Z`));
}

/**
 * A wall time already in the tenant zone (`HH:mm`, e.g. from `scheduled_at_local`)
 * in the locale's own time notation, e.g. `09:00`. No time zone shift: the value
 * is local already, only its punctuation is locale-formatted.
 */
export function formatLocalTime(localTime: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(new Date(`1970-01-01T${localTime}:00Z`));
}

/** A desired date with its optional desired time (E510), e.g. `5. Nov. 2026` or `5. Nov. 2026, 18:30`. */
export function formatDesired(localDate: string, localTime: string | null, context: FormatContext = current): string {
  if (!localTime) return formatCalendarDate(localDate, context);
  return new Intl.DateTimeFormat(context.locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(`${localDate}T${localTime}:00Z`));
}

/** A wall date-time already in the tenant zone (`YYYY-MM-DDTHH:mm`) fully formatted, e.g. `05.10.2026, 09:00`. */
export function formatLocalDateTime(localDateTime: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${localDateTime}:00Z`));
}

/** A wall date-time in the tenant zone with a short weekday, e.g. `2026-10-03T09:00` -> `Sa., 3. Okt., 09:00` (move toast, cancel text). */
export function formatLocalWeekdayDateTime(localDateTime: string, context: FormatContext = current): string {
  return new Intl.DateTimeFormat(context.locale, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(
    new Date(`${localDateTime}:00Z`),
  );
}

/** The current minute as wall time in the tenant zone (`YYYY-MM-DDTHH:mm`), e.g. to refuse a move into the past. */
export function nowLocalDateTime(context: FormatContext = current, now: Date = new Date()): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: context.timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** The tenant-zone calendar day (`YYYY-MM-DD`) of a UTC instant, for date pickers. */
export function localDateOf(isoUtc: string, context: FormatContext = current): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: context.timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(isoUtc))
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Today in the tenant time zone (`YYYY-MM-DD`), e.g. the `max` of a "last featured" picker. */
export function todayLocalDate(context: FormatContext = current): string {
  return localDateOf(new Date().toISOString(), context);
}

/**
 * IANA time zone identifiers for the tenant settings picker (spec 10 §6).
 * `Intl.supportedValuesOf` omits `UTC`, which the API accepts, and may lack the
 * stored zone; both are added so the current value is always selectable.
 */
export function listTimeZones(stored?: string): readonly string[] {
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [current.timeZone];
  return [...new Set([...zones, 'UTC', ...(stored ? [stored] : [])])].sort();
}

/** The tenant-zone UTC offset (ms) of `instant`, via a round trip through `Intl` parts. */
function offsetMillis(instant: Date, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - instant.getTime();
}

/**
 * The UTC instant of `localDate` (`YYYY-MM-DD`) at `hh:mm:ss` wall-clock time in
 * `timeZone`, plus `milliseconds`. The offset is resolved from a whole-second
 * instant only: `Intl.DateTimeFormat` (no `fractionalSecondDigits` requested)
 * can round a `.999` millisecond value up to the next second, which would shift
 * the resolved offset by a full second right at a DST boundary.
 */
function zonedWallTimeToUtc(localDate: string, hhmmss: string, milliseconds: number, timeZone: string): Date {
  const naiveUtc = new Date(`${localDate}T${hhmmss}Z`);
  return new Date(naiveUtc.getTime() - offsetMillis(naiveUtc, timeZone) + milliseconds);
}

/**
 * The UTC instant of the start of `localDate` (`YYYY-MM-DD`) in the tenant time
 * zone, for filters that accept a calendar day rather than a timestamp (spec 10
 * §6, audit log "from").
 */
export function startOfDayUtc(localDate: string, context: FormatContext = current): string {
  return zonedWallTimeToUtc(localDate, '00:00:00', 0, context.timeZone).toISOString();
}

/** The UTC instant of the inclusive end of `localDate` in the tenant time zone (audit log "to"). */
export function endOfDayUtc(localDate: string, context: FormatContext = current): string {
  return zonedWallTimeToUtc(localDate, '23:59:59', 999, context.timeZone).toISOString();
}

/**
 * Formats a money amount from its decimal string. `Intl.NumberFormat` formats
 * numeric strings exactly, so the amount never passes through a float.
 */
export function formatMoney(money: Money, fractionDigits = 2, context: FormatContext = current): string {
  if (!/^-?\d+(\.\d+)?$/.test(money.amount)) {
    throw new Error(`formatMoney: not a decimal string: ${money.amount}`);
  }
  return new Intl.NumberFormat(context.locale, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(money.amount as Intl.StringNumericLiteral);
}

/**
 * The display name of a BCP-47 language tag in the UI locale (`cs` in `en`: `Czech`),
 * falling back to the tag itself when the runtime does not know it.
 */
export function formatLanguage(tag: string, context: FormatContext = current): string {
  try {
    return new Intl.DisplayNames([context.locale], { type: 'language', fallback: 'code' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}

/**
 * The display name of an ISO 3166-1 alpha-2 country code in the UI locale
 * (`CZ` in `de-AT`: `Tschechien`), falling back to the code itself.
 */
export function formatCountry(code: string, context: FormatContext = current): string {
  try {
    return new Intl.DisplayNames([context.locale], { type: 'region', fallback: 'code' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** A list of display texts in the active locale without a conjunction ("a, b, c"), e.g. several translations in one cell. */
export function formatList(items: readonly string[], context: FormatContext = current): string {
  return new Intl.ListFormat(context.locale, { type: 'unit', style: 'short' }).format(items);
}

const FILE_SIZE_UNITS = [
  ['gigabyte', 1_000_000_000],
  ['megabyte', 1_000_000],
  ['kilobyte', 1_000],
] as const;

/**
 * A file size in decimal units (spec 30 §4.1: 50 MB = 50 000 000 bytes),
 * e.g. `3,5 MB`, `820 kB`, `12 byte` in `de-AT`.
 */
export function formatFileSize(bytes: number, context: FormatContext = current): string {
  const [unit, divisor] = FILE_SIZE_UNITS.find(([, size]) => bytes >= size) ?? (['byte', 1] as const);
  return new Intl.NumberFormat(context.locale, { style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: 1 }).format(bytes / divisor);
}

/** A share 0..1 as a whole percentage, e.g. `40 %` in `de-AT`. */
export function formatPercent(ratio: number, context: FormatContext = current): string {
  return new Intl.NumberFormat(context.locale, { style: 'percent', maximumFractionDigits: 0 }).format(Math.max(0, Math.min(1, ratio)));
}

/**
 * A short duration always in seconds, e.g. `22,5 Sek.` and `90 Sek.` in `de-AT`
 * (reel lengths and their limits, spec 100 section 6.1: "42,5 s von hoechstens 90 s").
 */
export function formatSeconds(milliseconds: number, context: FormatContext = current): string {
  return new Intl.NumberFormat(context.locale, { style: 'unit', unit: 'second', unitDisplay: 'short', maximumFractionDigits: 1 }).format(Math.max(0, milliseconds) / 1000);
}

/**
 * A media duration, e.g. `12,4 Sek.` below a minute and `3,5 Min.` above in `de-AT`
 * (spec 30 §6.4 "Technisch"); unit texts come from `Intl`, not from code.
 */
export function formatDuration(milliseconds: number, context: FormatContext = current): string {
  const seconds = Math.max(0, milliseconds) / 1000;
  const [unit, value] = seconds < 60 ? (['second', seconds] as const) : (['minute', seconds / 60] as const);
  return new Intl.NumberFormat(context.locale, { style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: 1 }).format(value);
}
